import { apiFetch } from "./index";
import { getApiBaseUrl } from "./baseUrl";

const API_BASE = getApiBaseUrl();

const AI_TALK_PATH = "/api/management/ai-talk";
const AI_TALK_AUDIO_PATH = "/api/management/ai-talk-withAudio";

export interface AITalk {
  answer: string;
  chat_historiesId: string;
  created_at: string;
}

export interface AITalkStreamStatus {
  phase: "queued" | "generating";
  message: string;
  position?: number;
}

export interface AITalkOptions {
  /** 未来ボードのカードの会話（agent は future_board にする。postFutureTalk を使う） */
  futureCardId?: number;
}

export interface AITalkStatusResponse {
  isLimitExceeded: boolean;
  maxChats: number;
  currentChats?: number;
}

// 1日のAIトーク利用状況（制限ステータス）を取得する
export const fetchAITalkStatus = async (token: string): Promise<AITalkStatusResponse> => {
  return await apiFetch<AITalkStatusResponse>(`${API_BASE}${AI_TALK_PATH}/status`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * AIトーク: テキスト送信（SSEストリーミング）
 *
 * @param onToken 任意。トークンが届くたびに呼ばれる。
 *                (chunk: 今回のトークン, accumulated: ここまでの全文)
 *                省略時は逐次描画なしで、最終的な AITalk のみ resolve する（後方互換）。
 */
export const postAITalk = async (
  token: string,
  agent: string,
  question: string,
  onToken?: (chunk: string, accumulated: string) => void,
  onStatus?: (status: AITalkStreamStatus) => void,
  options: AITalkOptions = {},
): Promise<AITalk> => {
  const url = `${API_BASE}${AI_TALK_PATH}`;

  // SSE専用のパースが必要なため、apiFetch(Core) は使わずに標準の fetch を使う
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(options.futureCardId ? { agent, question, future_card_id: options.futureCardId } : { agent, question }),
  });

  // SSE開始前のエラー（400 / 403 など）は JSON で返ってくる
  if (!res.ok) {
    const errorText = await res.text().catch(() => "");
    try {
      const errJson = JSON.parse(errorText);
      if (errJson?.isLimitExceeded) {
        const limitError = new Error(errJson.error) as Error & { isLimitExceeded?: boolean };
        limitError.isLimitExceeded = true;
        throw limitError;
      }
      if (errJson?.error) throw new Error(errJson.error);
    } catch (e) {
      if ((e as { isLimitExceeded?: boolean })?.isLimitExceeded) throw e;
      // JSONでない場合は下の汎用エラーへ
    }
    throw new Error(`API error: ${res.status} ${errorText}`);
  }

  if (!res.body) throw new Error("ストリームを取得できませんでした");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer = "";
  let result: AITalk | null = null;

  // \n\n 区切りの1イベントを処理する
  const handleEvent = (raw: string) => {
    // ": heartbeat" などコメント行・空行は data: を含まないので無視される
    const dataLines = raw.split("\n").filter((l) => l.startsWith("data:"));
    if (dataLines.length === 0) return;
    const jsonStr = dataLines.map((l) => l.slice(5).trim()).join("");
    if (!jsonStr) return;

    let parsed: any;
    try {
      parsed = JSON.parse(jsonStr);
    } catch {
      return; // 分割途中の不完全データ（通常は来ない）
    }

    switch (parsed.type) {
      case "token":
        answer += parsed.token ?? "";
        onToken?.(parsed.token ?? "", answer);
        break;
      case "done":
        result = {
          answer: parsed.answer ?? answer,
          chat_historiesId: parsed.chat_historiesId,
          created_at: parsed.created_at ?? new Date().toISOString(),
        };
        break;
      case "status":
        onStatus?.({
          phase: parsed.phase === "queued" ? "queued" : "generating",
          message: parsed.message ?? "回答を準備しています。",
          position: typeof parsed.position === "number" ? parsed.position : undefined,
        });
        break;
      case "error":
        {
          const streamError = new Error(parsed.error ?? "サーバー内部エラーが発生しました") as Error & { retryable?: boolean; code?: string };
          streamError.retryable = Boolean(parsed.retryable);
          streamError.code = parsed.code;
          throw streamError;
        }
      default:
        // 旧形式 { answer, chat_historiesId } / { error } へのフォールバック
        if (parsed.error) throw new Error(parsed.error);
        if (parsed.answer != null) {
          result = {
            answer: parsed.answer,
            chat_historiesId: parsed.chat_historiesId,
            created_at: parsed.created_at ?? new Date().toISOString(),
          };
        }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split("\n\n");
    buffer = events.pop() ?? ""; // 末尾の未完分は次回へ持ち越す
    for (const ev of events) handleEvent(ev);
  }
  // ストリーム終端に残ったバッファを処理
  if (buffer.trim()) handleEvent(buffer);

  if (!result) throw new Error("AIからの回答データが見つかりませんでした");
  return result;
};

/**
 * 未来ボードの「この未来について話す」。カードの最新の保存版を、サーバーが文脈にする。
 * 送信前の拒否（無効・上限・実行中・カードなし）は、"API error: <状態> <本文>" の例外（parseFutureBoardError で分類）。
 */
export const postFutureTalk = (
  token: string,
  cardId: number,
  question: string,
  onToken?: (chunk: string, accumulated: string) => void,
  onStatus?: (status: AITalkStreamStatus) => void,
): Promise<AITalk> => postAITalk(token, "future_board", question, onToken, onStatus, { futureCardId: cardId });

export const fetchAITalkAudio = async (
  token: string,
  agent: string,
  chat_historiesId: string,
  format: "mp4" | "webm"
): Promise<Response> => {
  const res = await fetch(`${API_BASE}${AI_TALK_AUDIO_PATH}?format=${encodeURIComponent(format)}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ agent, chat_historiesId }),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`audio api error: ${res.status} ${t}`);
  }
  return res;
};

export const playAudioStreamAuto = async (
  token: string,
  agent: string,
  chat_historiesId: string,
  audio: HTMLAudioElement
) => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const mime = isSafari ? 'audio/mp4; codecs="mp4a.40.2"' : 'audio/webm; codecs="opus"';
  const format: "mp4" | "webm" = isSafari ? "mp4" : "webm";

  if (!MediaSource.isTypeSupported(mime)) {
    throw new Error(`このブラウザは ${mime} をサポートしていません`);
  }

  const res = await fetchAITalkAudio(token, agent, chat_historiesId, format);
  if (!res.body) throw new Error("音声ストリームが受信できません");

  const mediaSource = new MediaSource();
  audio.pause();
  audio.src = URL.createObjectURL(mediaSource);
  audio.load();

  mediaSource.addEventListener("sourceopen", async () => {
    const sourceBuffer = mediaSource.addSourceBuffer(mime);
    const reader = res.body!.getReader();
    const queue: any[] = [];

    const waitForUpdateEnd = () =>
      new Promise<void>((resolve) =>
        sourceBuffer.addEventListener("updateend", () => resolve(), { once: true })
      );

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) queue.push(value);

      if (!sourceBuffer.updating && queue.length > 0) {
        sourceBuffer.appendBuffer(queue.shift()!);
        await waitForUpdateEnd();
      }
    }

    while (queue.length > 0) {
      if (!sourceBuffer.updating) {
        sourceBuffer.appendBuffer(queue.shift()!);
      }
      await waitForUpdateEnd();
    }

    const endStream = () => {
      if (!sourceBuffer.updating && mediaSource.readyState === "open") {
        mediaSource.endOfStream();
      } else {
        sourceBuffer.addEventListener("updateend", endStream, { once: true });
      }
    };
    endStream();

    try {
      await audio.play();
    } catch (err) {
      console.error("audio.play() error", err);
    }
  });
};
